import { apiClient } from './client'
import { ApiError } from './errors'
import { usersApi } from './users'
import type { AccessRole, TemplateAccess, TemplateAccessEntry } from '@/types/domain'

const http = () => apiClient.axios

/**
 * Template sharing and folder membership.
 *
 * Two vocabularies meet here and neither can be dropped. A template's own
 * grants are read/write/admin (`access_control_entries`); a folder's are
 * viewer/contributor/manager, and the gateway maps those onto the former
 * when it reports a template's access. `GET …/access` therefore returns both
 * kinds in one list, told apart by `source`.
 */

/** A folder-scoped role. Not the same ladder as a template's `AccessRole`;
 *  the gateway maps viewer→read, contributor→write, manager→admin when it
 *  reports folder-inherited access on a template. */
export type FolderRole = 'viewer' | 'contributor' | 'manager'

/** The legacy three-list shape `PUT /v1/templates/:id/access` still takes.
 *  A uid must appear in at most one list — the gateway builds a map and the
 *  last list wins, silently. */
export interface AccessControlLists {
  read: string[]
  write: string[]
  admin: string[]
}

/**
 * What the caller was trying to do, because one code means two different
 * things here: `owner_access_required` answers both "you cannot remove the
 * owner" (DELETE) and "you cannot change the owner's role" (PATCH), and a
 * dialog that says the wrong one of those is telling the person something
 * untrue about what they just clicked.
 */
type AccessAction = 'remove' | 'role' | 'other'

/** German messages for the refusals these endpoints produce. The gateway
 *  sends the code in `error` rather than `code` on several of them, so
 *  `ApiError.code` is unset and the raw message is a machine string. */
function mapAccessError(error: unknown, action: AccessAction = 'other'): ApiError {
  const api = ApiError.from(error)
  const code = api.code ?? api.message
  switch (code) {
    case 'access_entry_not_found':
      return new ApiError({
        status: api.status,
        code: 'access_entry_not_found',
        // Not "no access": the person may well have access through the
        // folder. What they have not got is an entry on this template, and
        // giving them one is an invitation, not a role change.
        message:
          'Diese Person hat keine eigene Freigabe für diese Vorlage. Laden Sie sie ein, um ihr eine Rolle zu geben.',
        details: api.details,
        cause: api.cause,
      })
    case 'user_not_found':
      return new ApiError({
        status: api.status,
        code: 'user_not_found',
        message: 'Zu dieser E-Mail-Adresse gibt es kein Konto.',
        details: api.details,
        cause: api.cause,
      })
    case 'owner_access_required':
      return new ApiError({
        status: api.status,
        code: 'owner_access_required',
        message:
          action === 'role'
            ? 'Die Rolle des Besitzers der Vorlage kann nicht geändert werden.'
            : 'Der Besitzer der Vorlage kann nicht entfernt werden.',
        details: api.details,
        cause: api.cause,
      })
    default:
      if (api.isForbidden) {
        return new ApiError({
          status: api.status,
          code: api.code ?? 'forbidden',
          message: 'Sie dürfen die Freigabe dieser Vorlage nicht ändern.',
          details: api.details,
          cause: api.cause,
        })
      }
      return api
  }
}

/**
 * The three lists `PUT …/access` replaces, built from the entries a `GET`
 * returned.
 *
 * Only `source: 'template'` entries go in. A folder-inherited membership is
 * not an entry on the template at all — writing it back would turn it into a
 * direct grant that outlives the person's folder membership, which is not
 * what anyone clicking a role picker asked for.
 *
 * The owner is kept. Their row may be synthesised (the creator is listed
 * even with no entry of their own), so writing it back can create an admin
 * entry that did not exist; that is harmless — their access comes from
 * `created_by` either way — while dropping it would delete a real entry.
 */
export function accessControlFrom(entries: TemplateAccessEntry[]): AccessControlLists {
  const lists: AccessControlLists = { read: [], write: [], admin: [] }
  for (const entry of entries) {
    if (entry.source !== 'template') continue
    lists[entry.role].push(entry.userId)
  }
  return lists
}

/** Whether `DELETE …/access/:userId` can do anything for this row. A
 *  folder-inherited membership is removed in the folder, and the owner
 *  cannot be removed at all (409 `owner_access_required`). */
export function canRemoveEntry(entry: TemplateAccessEntry): boolean {
  return entry.source === 'template' && !entry.owner
}

export const accessApi = {
  async get(templateId: string): Promise<TemplateAccess> {
    const { data } = await http().get<TemplateAccess>(`/v1/templates/${templateId}/access`)
    return { entries: data.entries ?? [], canManage: data.canManage ?? false }
  },

  /**
   * Replaces every direct entry on the template. Anyone left out loses their
   * access — including anyone invited after the list this was built from was
   * read.
   *
   * Only for an explicit bulk save, where replacing the list is what the
   * person asked for. A single role change is `setRole`.
   */
  async replace(templateId: string, accessControl: AccessControlLists): Promise<void> {
    try {
      await http().put(`/v1/templates/${templateId}/access`, { access_control: accessControl })
    } catch (error) {
      throw mapAccessError(error)
    }
  },

  /**
   * Changes one person's role, and nothing else.
   *
   * One upsert against `PATCH …/access/:userId`. This used to read the whole
   * list, change the one row and write it all back through `PUT …/access`,
   * which is a full replace: an invite that landed between the read and the
   * write was deleted by the save, and the second writer never saw the row
   * it destroyed. With the PATCH, two people changing two different users'
   * roles at once both keep their change and a concurrent invite survives.
   *
   * It grants nothing: a person with no direct entry — including one who is
   * a member through the folder — is `access_entry_not_found`, and giving
   * them a template-specific role goes through `invite`.
   */
  async setRole(templateId: string, userId: string, role: AccessRole): Promise<void> {
    try {
      await http().patch(`/v1/templates/${templateId}/access/${userId}`, { role })
    } catch (error) {
      throw mapAccessError(error, 'role')
    }
  },

  /** Adds or raises one person by e-mail, without touching anyone else.
   *  There is no e-mail invitation yet: an address with no account is a 404
   *  (`user_not_found`). */
  async invite(
    templateId: string,
    input: { email: string; role: AccessRole },
  ): Promise<void> {
    try {
      await http().post(`/v1/templates/${templateId}/access/invite`, {
        email: input.email,
        role: input.role,
      })
    } catch (error) {
      throw mapAccessError(error)
    }
  },

  /** Removes one direct entry. A folder-inherited membership is unaffected
   *  by this — see `canRemoveEntry`. */
  async remove(templateId: string, userId: string): Promise<void> {
    try {
      await http().delete(`/v1/templates/${templateId}/access/${userId}`)
    } catch (error) {
      throw mapAccessError(error, 'remove')
    }
  },

  // -- Folder membership ----------------------------------------------------

  /** uid → role. Empty for a folder that still carries only the legacy ACL
   *  lists; the gateway synthesises the map from them in that case. */
  async folderMembers(folderId: string): Promise<Record<string, FolderRole>> {
    const { data } = await http().get<{ members?: Record<string, FolderRole> }>(
      `/v1/folders/${folderId}/members`,
    )
    return data.members ?? {}
  },

  async setFolderMember(folderId: string, uid: string, role: FolderRole): Promise<void> {
    try {
      await http().put(`/v1/folders/${folderId}/members/${uid}`, { role })
    } catch (error) {
      throw mapAccessError(error)
    }
  },

  async removeFolderMember(folderId: string, uid: string): Promise<void> {
    try {
      await http().delete(`/v1/folders/${folderId}/members/${uid}`)
    } catch (error) {
      throw mapAccessError(error)
    }
  },
}

/** Resolves an e-mail to a uid before the invite is offered, returning null
 *  when no account has it. The implementation lives in `users.ts`; it is
 *  re-exported here so the share dialog has one import. */
export const lookupUserByEmail = usersApi.lookupByEmail
